import axios from 'axios';
import { formatUsPhone, maskUsPhone } from 'utilities/formatPhone';
import React, { useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { InputSwitch } from 'primereact/inputswitch';
import { Dropdown } from 'primereact/dropdown';
import '../../../styles/addCertificateHolder.css';

declare global {
  interface Window {
    google: any;
  }
}

export type EditableHolder = {
  databaseId: string;
  name: string;
  eMail: string;
  cellPhone: string;
  phone: string;
  fax: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateName: string;
  zipCode: string;
};

export type AddCertificateHolderProps = {
  visible: boolean;
  onHide: () => void;
  insuredDatabaseId?: string;
  editHolder?: EditableHolder | null;
};


const US_STATES = [
  { label: 'Alabama', value: 'AL' }, { label: 'Alaska', value: 'AK' }, { label: 'Arizona', value: 'AZ' },
  { label: 'Arkansas', value: 'AR' }, { label: 'California', value: 'CA' }, { label: 'Colorado', value: 'CO' },
  { label: 'Connecticut', value: 'CT' }, { label: 'Delaware', value: 'DE' }, { label: 'Florida', value: 'FL' },
  { label: 'Georgia', value: 'GA' }, { label: 'Hawaii', value: 'HI' }, { label: 'Idaho', value: 'ID' },
  { label: 'Illinois', value: 'IL' }, { label: 'Indiana', value: 'IN' }, { label: 'Iowa', value: 'IA' },
  { label: 'Kansas', value: 'KS' }, { label: 'Kentucky', value: 'KY' }, { label: 'Louisiana', value: 'LA' },
  { label: 'Maine', value: 'ME' }, { label: 'Maryland', value: 'MD' }, { label: 'Massachusetts', value: 'MA' },
  { label: 'Michigan', value: 'MI' }, { label: 'Minnesota', value: 'MN' }, { label: 'Mississippi', value: 'MS' },
  { label: 'Missouri', value: 'MO' }, { label: 'Montana', value: 'MT' }, { label: 'Nebraska', value: 'NE' },
  { label: 'Nevada', value: 'NV' }, { label: 'New Hampshire', value: 'NH' }, { label: 'New Jersey', value: 'NJ' },
  { label: 'New Mexico', value: 'NM' }, { label: 'New York', value: 'NY' }, { label: 'North Carolina', value: 'NC' },
  { label: 'North Dakota', value: 'ND' }, { label: 'Ohio', value: 'OH' }, { label: 'Oklahoma', value: 'OK' },
  { label: 'Oregon', value: 'OR' }, { label: 'Pennsylvania', value: 'PA' }, { label: 'Rhode Island', value: 'RI' },
  { label: 'South Carolina', value: 'SC' }, { label: 'South Dakota', value: 'SD' }, { label: 'Tennessee', value: 'TN' },
  { label: 'Texas', value: 'TX' }, { label: 'Utah', value: 'UT' }, { label: 'Vermont', value: 'VT' },
  { label: 'Virginia', value: 'VA' }, { label: 'Washington', value: 'WA' }, { label: 'West Virginia', value: 'WV' },
  { label: 'Wisconsin', value: 'WI' }, { label: 'Wyoming', value: 'WY' }
];

const AddCertificateHolder: React.FC<AddCertificateHolderProps> = ({ visible, onHide, insuredDatabaseId, editHolder }) => {
    const isEdit = !!editHolder;
    const [loading, setLoading] = React.useState(false);
    const handleSaveCertificateHolder = async () => {
      setLoading(true);
      const NCToken = localStorage.getItem('NCToken');
      const igtoken = localStorage.getItem('igtoken');
      const payload = {
        databaseId: isEdit ? editHolder!.databaseId : (insuredDatabaseId || ''),
        name,
        stateAbbrevation: state || '',
        city: city || '',
        addressLine1: address1 || '',
        addressLine2: address2 || '',
        zipCode: zip || '',
        phone: phone || '',
        cellPhone: cellPhone || '',
        fax: fax || '',
        eMail: email || '',
      };
      try {
        await axios.post(
          `${process.env.REACT_APP_COIPortal_Url}/api/IGIGCOI/certificate-holder`,
          payload,
          {
            headers: {
              'NCToken': NCToken || '',
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Authorization': `Bearer ${igtoken || ''}`,
            },
          }
        );
        toast.current?.show({ severity: 'success', summary: 'Success', detail: isEdit ? 'Certificate holder updated successfully!' : 'Certificate holder added successfully!', life: 3000 });
        setTimeout(() => {
          handleHide();
        }, 1000);
      } catch (err: any) {
        const errorMsg = err.response?.data ? (typeof err.response.data === 'string' ? err.response.data : JSON.stringify(err.response.data)) : err.message;
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to add certificate holder: ' + errorMsg, life: 4000 });
      } finally {
        setLoading(false);
      }
    };
  const [name, setName] = React.useState('');
  const toast = useRef<Toast>(null);
  const [email, setEmail] = React.useState('');
  const [hideFromInsured, setHideFromInsured] = React.useState(false);
  const [cellPhone, setCellPhone] = React.useState('');
  const [phone, setPhone] = React.useState('');
  const [fax, setFax] = React.useState('');
  const [address1, setAddress1] = React.useState('');
  const [address2, setAddress2] = React.useState('');
  const [city, setCity] = React.useState('');
  const [state, setState] = React.useState<string | null>(null);
  const [zip, setZip] = React.useState('');
  const [addressSuggestions, setAddressSuggestions] = React.useState<any[]>([]);
  const [addressLoading, setAddressLoading] = React.useState(false);
  const address1Ref = React.useRef<HTMLInputElement>(null);
  const debounceTimeout = React.useRef<NodeJS.Timeout | null>(null);


  const handleHide = () => {
    setName('');
    setEmail('');
    setHideFromInsured(false);
    setCellPhone('');
    setPhone('');
    setFax('');
    setAddress1('');
    setAddress2('');
    setCity('');
    setState(null);
    setZip('');
    setAddressSuggestions([]);
    setAddressLoading(false);
    onHide();
  };

  // Prefill form when opening for edit
  React.useEffect(() => {
    if (visible && editHolder) {
      setName(editHolder.name || '');
      setEmail(editHolder.eMail || '');
      setCellPhone(formatUsPhone(editHolder.cellPhone || ''));
      setPhone(formatUsPhone(editHolder.phone || ''));
      setFax(formatUsPhone(editHolder.fax || ''));
      setAddress1(editHolder.addressLine1 || '');
      setAddress2(editHolder.addressLine2 || '');
      setCity(editHolder.city || '');
      setZip(editHolder.zipCode || '');
      // Map stateName → abbreviation
      const match = US_STATES.find(s => s.label.toLowerCase() === (editHolder.stateName || '').toLowerCase());
      setState(match ? match.value : null);
    }
  }, [visible, editHolder]);

  const handleAddress1Change = React.useCallback((value: string) => {
    setAddress1(value);
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    if (value.length > 2 && window.google && window.google.maps && window.google.maps.places) {
      setAddressLoading(true);
      debounceTimeout.current = setTimeout(() => {
        const autocompleteService = new window.google.maps.places.AutocompleteService();
        autocompleteService.getPlacePredictions(
          { input: value, types: ['address'] },
          (predictions: any[]) => {
            setAddressSuggestions(predictions || []);
            setAddressLoading(false);
          }
        );
      }, 300);
    } else {
      setAddressSuggestions([]);
      setAddressLoading(false);
    }
  }, []);

  const selectAddressSuggestion = React.useCallback((prediction: any) => {
    setAddress1(prediction.description);
    setAddressSuggestions([]);
    setAddressLoading(false);
    if (window.google && window.google.maps && window.google.maps.places) {
      const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
      placesService.getDetails({ placeId: prediction.place_id }, (place: any, status: any) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          fillAddressFields(place);
        }
      });
    }
  }, []);

  function fillAddressFields(place: any) {
    let streetNumber = '';
    let route = '';
    let cityVal = '';
    let stateVal = '';
    let zipVal = '';
    let address1Val = '';
    let address2Val = '';

    if (place.address_components) {
      place.address_components.forEach((component: any) => {
        const types = component.types;
        if (types.includes('street_number')) {
          streetNumber = component.long_name;
        }
        if (types.includes('route')) {
          route = component.long_name;
        }
        if (types.includes('locality') || types.includes('administrative_area_level_3')) {
          cityVal = component.long_name;
        }
        if (types.includes('administrative_area_level_1')) {
          stateVal = component.short_name;
        }
        if (types.includes('postal_code')) {
          zipVal = component.long_name;
        }
        if (types.includes('subpremise')) {
          address2Val = component.long_name;
        }
      });
    }

    address1Val = place.formatted_address || `${streetNumber} ${route}`.trim();
    setAddress1(address1Val);
    setCity(cityVal);
    setState(stateVal);
    setZip(zipVal);
    if (address2Val) setAddress2(address2Val);
  }

  React.useEffect(() => {
    if (!visible) return;
    if (!window.google || !window.google.maps) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }, [visible]);

  return (
    <>
      <Toast ref={toast} position="top-right" />
      <Dialog
        header={<span style={{ color: '#ba170b', fontWeight: 600 }}>{isEdit ? 'Edit Certificate Holder' : 'Add Certificate Holder'}</span>}
        visible={visible}
        style={{ width: "900px", maxWidth: "98vw" }}
        onHide={handleHide}
        modal
        className="addCertHolderDialog"
      >
      <div style={{ marginBottom: 12, padding: 10 }}>
        <div className="page__muted" style={{ marginBottom: 4 }}>
          Enter details for the new certificate holder.
        </div>
      </div>
      <div
        className="page__twoCol"
        style={{ gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 12 }}
      >
        <div className="pageCard" style={{ padding: 16 }}>
          <div style={{ color: '#002A5C', fontWeight: 500 }}>Name <span style={{ color: 'red' }}>*</span></div>
          <InputText maxLength={100}
            value={name}
            placeholder="Please enter name"
            onChange={(e) => setName(e.target.value)}
            style={{ width: "100%", marginBottom: 16 }}
          />
          <div style={{ color: '#002A5C', fontWeight: 500 }}>Email Address</div>
          <InputText maxLength={100}
            value={email}
            placeholder="Please enter email"
            onChange={(e) => setEmail(e.target.value)}
            style={{ width: "100%", marginBottom: 16 }}
          />
          <div style={{ color: '#002A5C', fontWeight: 500 }}>Cell Phone</div>
          <InputText maxLength={20}
            value={cellPhone}
            placeholder="Please enter cell phone"
            onChange={(e) => setCellPhone(maskUsPhone(e.target.value))}
            style={{ width: "100%", marginBottom: 16 }}
          />
          <div style={{ color: '#002A5C', fontWeight: 500 }}>Phone</div>
          <InputText maxLength={20}
            value={phone}
            placeholder="Please enter phone"
            onChange={(e) => setPhone(maskUsPhone(e.target.value))}
            style={{ width: "100%", marginBottom: 16 }}
          />
          <div style={{ color: '#002A5C', fontWeight: 500 }}>Fax</div>
          <InputText maxLength={20}
            value={fax}
            placeholder="Please enter fax"
            onChange={(e) => setFax(maskUsPhone(e.target.value))}
            style={{ width: "100%", marginBottom: 16 }}
          />
          {/* <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <InputSwitch checked={hideFromInsured} onChange={e => setHideFromInsured(e.value ?? false)} />
     <span style={{ color: '#002A5C', fontWeight: 500, marginLeft: 8 }}>Hide from insured</span>
  
          </div> */}
        </div>
        <div className="pageCard" style={{ padding: 16 }}>
          <div style={{ color: '#002A5C', fontWeight: 500 }}>Address Line 1</div>
          <div style={{ position: 'relative', width: '100%' }}>
            <InputText maxLength={100}
              ref={address1Ref}
              value={address1}
              placeholder="Please enter address line 1"
              onChange={(e) => handleAddress1Change(e.target.value)}
              style={{ width: "100%", marginBottom: 16, paddingRight: addressLoading ? 32 : undefined }}
              autoComplete="off"
            />
            {addressLoading && (
              <span style={{
                position: 'absolute',
                right: 10,
                top: '40%',
                transform: 'translateY(-50%)',
                zIndex: 1200
              }}>
                <i className="pi pi-spin pi-spinner" style={{ fontSize: 18, color: '#0057A4' }} />
              </span>
            )}
            {addressSuggestions.length > 0 && !addressLoading && (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, border: '1px solid #ccc', maxHeight: '200px', overflowY: 'auto', background: 'white', position: 'absolute', zIndex: 1000, width: '100%' }}>
                {addressSuggestions.map((suggestion, index) => (
                  <li
                    key={index}
                    onClick={() => selectAddressSuggestion(suggestion)}
                    style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid #eee' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    {suggestion.description}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div style={{ color: '#002A5C', fontWeight: 500 }}>Address Line 2</div>
          <InputText maxLength={100}
            value={address2}
            placeholder="Please enter address line 2"
            onChange={(e) => setAddress2(e.target.value)}
            style={{ width: "100%", marginBottom: 16 }}
          />
          <div style={{ color: '#002A5C', fontWeight: 500 }}>City</div>
          <InputText maxLength={100}
            value={city}
            placeholder="Please enter city"
            onChange={(e) => setCity(e.target.value)}
            style={{ width: "100%", marginBottom: 16 }}
          />
          <div style={{ color: '#002A5C', fontWeight: 500 }}>State</div>
          <Dropdown
            value={state}
            options={US_STATES}
            onChange={e => setState(e.value)}
            placeholder="Please select state"
            showClear
            filter
            filterPlaceholder="Search state..."
            optionLabel="label"
            style={{ width: "100%", marginBottom: 16 }}
            emptyMessage="No available options"
            emptyFilterMessage="No available options"
          />
          <div style={{ color: '#002A5C', fontWeight: 500 }}>ZIP/Postal Code</div>
          <InputText maxLength={20}
            value={zip}
            placeholder="Please enter ZIP/postal code"
            onChange={(e) => setZip(e.target.value)}
            style={{ width: "100%", marginBottom: 16 }}
          />
        </div>
      </div>
      <div className="addCertHolder__actions">
        <button
          type="button"
          className="p-button p-component appButton--secondary "
          onClick={handleHide}
        >
          <span>Cancel</span>
        </button>
        <button
          type="button"
          className="p-button p-component appButton--primary"
          style={{ background: '#0057A4', borderColor: '#0057A4', color: '#fff' }}
          disabled={!name.trim() || loading}
          onClick={handleSaveCertificateHolder}
        >
          {loading ? <span className="p-button-icon p-button-loading" style={{ marginRight: 8 }}><i className="pi pi-spin pi-spinner" /></span> : null}
          <span>Save</span>
        </button>

      </div>
      </Dialog>
    </>
  );
};

export default AddCertificateHolder;
